import * as React from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "./button";

type SplitViewProps = React.ComponentProps<"div"> & {
  /** Width of the list pane at md+ breakpoints. Defaults to 375. */
  sidebarWidth?: number | string;
};

function SplitView({ className, sidebarWidth = 375, style, children, ...props }: SplitViewProps) {
  const sidebar = typeof sidebarWidth === "number" ? `${sidebarWidth}px` : sidebarWidth;

  return (
    <div
      data-slot="split-view"
      className={cn(
        "grid grid-cols-1 overflow-hidden min-h-0 bg-transparent border-2 border-slate-300 shadow-md rounded-xl",
        "md:grid-cols-[var(--split-view-sidebar)_1fr]",
        className
      )}
      style={{ ...style, ["--split-view-sidebar" as string]: sidebar }}
      {...props}
    >
      {children}
    </div>
  );
}

type SplitViewListProps = React.ComponentProps<"div"> & {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  headerClassName?: string;
  footerClassName?: string;
  bodyClassName?: string;
};

function SplitViewList({
  className,
  header,
  footer,
  headerClassName,
  footerClassName,
  bodyClassName,
  children,
  ...props
}: SplitViewListProps) {
  return (
    <div
      data-slot="split-view-list"
      className={cn("flex flex-col overflow-hidden h-full min-h-0 bg-transparent border-r border-slate-200", className)}
      {...props}
    >
      {header != null && (
        <div data-slot="split-view-list-header" className={cn("shrink-0 border-b border-slate-100", headerClassName)}>
          {header}
        </div>
      )}
      <div
        data-slot="split-view-list-body"
        className={cn("flex-1 overflow-y-auto no-scrollbar min-h-0 bg-slate-50", bodyClassName)}
      >
        {children}
      </div>
      {footer != null && (
        <div
          data-slot="split-view-list-footer"
          className={cn("shrink-0 border-t border-slate-100 bg-white", footerClassName)}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

type SplitViewDetailProps = React.ComponentProps<"div"> & {
  title?: React.ReactNode;
  onClose?: () => void;
  /** Custom header replaces the default title/close bar when provided. */
  header?: React.ReactNode;
  headerClassName?: string;
  bodyClassName?: string;
};

function SplitViewDetail({
  className,
  title,
  onClose,
  header,
  headerClassName,
  bodyClassName,
  children,
  ...props
}: SplitViewDetailProps) {
  const showDefaultHeader = header == null && (title != null || onClose != null);
  const resolvedHeader =
    header != null ? (
      header
    ) : showDefaultHeader ? (
      <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-2">
        {title != null ? (
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">{title}</h3>
        ) : (
          <span />
        )}
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    ) : null;

  return (
    <div
      data-slot="split-view-detail"
      className={cn("flex flex-col overflow-hidden h-full min-w-0 bg-slate-50", className)}
      {...props}
    >
      {resolvedHeader != null && (
        <div data-slot="split-view-detail-header" className={cn("shrink-0", headerClassName)}>
          {resolvedHeader}
        </div>
      )}
      <div
        data-slot="split-view-detail-body"
        className={cn("flex-1 overflow-y-auto flex flex-col gap-6 min-h-0 p-4 pb-12", bodyClassName)}
      >
        {children}
      </div>
    </div>
  );
}

const SplitViewNamespace = Object.assign(SplitView, {
  List: SplitViewList,
  Detail: SplitViewDetail,
});

export { SplitViewNamespace as SplitView };
